/**
 * The project set-up wizard (batch 6: F13 project set-up for the office, F26 "so every setting and detail is right
 * for the field crew"; docs/specs/cloud/project-setup-wizard.md) and the office's set-up as Teslyn Field's
 * Quick-start and training page shows it (R125, decision 846).
 *
 * The wizard runs in Teslyn Cloud, one step per part of the project, each step running the quick-start checklist's own
 * checks for its part (quick-start-checklist.ts §9: no rule written twice) on the cloud's rows, plus the client
 * portal step's check here. The office confirms a step once nothing in it fails; confirmations are kept per project
 * (`project_setup_step`) with who and when. The project folder's checks cannot be made in the cloud and say so; the
 * field crew's first flight wizard (first-flight.ts) checks them on the laptop.
 */
import {
  CHECKLIST_STATE_WORD,
  quickStartChecklist,
  type ChecklistInput,
  type ChecklistItemId,
  type ChecklistState,
} from './quick-start-checklist.js';
import { reportEmailSettingWords, type ReportEmailSetting } from './app-email.js';

export type ProjectSetupStepId =
  'blocks' | 'heights' | 'staff' | 'aircraft' | 'basemag' | 'alerts' | 'client' | 'laptop';

export interface ProjectSetupStepDef {
  readonly id: ProjectSetupStepId;
  readonly title: string;
  /** What the step is for, one or two sentences (the step's opening words). */
  readonly intro: string;
  /** The quick-start checks the step runs. */
  readonly checks: readonly ChecklistItemId[];
}

/** The wizard's steps, in order. */
export const PROJECT_SETUP_STEPS: readonly ProjectSetupStepDef[] = [
  {
    id: 'blocks',
    title: 'Blocks and zone',
    intro:
      "The survey blocks come from the contract. Their zone comes from each block's lines file once the final flight plan is imported in Teslyn Field.",
    checks: ['project.zone'],
  },
  {
    id: 'heights',
    title: 'Heights and QC limits',
    intro:
      "Each survey block's survey height and the contract's terrain clearance and flight path limits. The crew sets the hard deck and lower working height after the safety recce flight.",
    checks: ['project.heights', 'project.qc_settings'],
  },
  {
    id: 'staff',
    title: 'Staff and crew',
    intro:
      'The pilots and operators in the staff register, and who is on this project when. Alerts and the daily entries use the crew.',
    checks: ['staff.register', 'staff.crew'],
  },
  {
    id: 'aircraft',
    title: 'Aircraft and trackers',
    intro:
      'The aircraft with the figures the maintenance release and fuel need, and a tracker on each one for live tracking and the SOS and lost-tracking alerts.',
    checks: ['aircraft.register', 'aircraft.tracker'],
  },
  {
    id: 'basemag',
    title: 'Base magnetometer',
    intro:
      "Where the base magnetometer stands, and the TA Live unit linked to the project for Teslyn Cloud's diurnal QC.",
    checks: ['basemag.position', 'basemag.station'],
  },
  {
    id: 'alerts',
    title: 'Alerts',
    intro:
      'Who receives the basemag alerts: the operators on the crew. Each person turns push on for their own phone.',
    checks: ['alerts.operators'],
  },
  {
    id: 'client',
    title: 'Client portal and report emails',
    intro:
      "The client organisation that sees the project's published reports, refly list and photos, its people, and whether they are emailed when a report is published.",
    checks: [],
  },
  {
    id: 'laptop',
    title: 'On the field laptop',
    intro:
      "The project folder's flight plan, boundaries and Geosoft databases, and Diurnal QC, are checked in Teslyn Field on the laptop that holds the folder. The crew's first flight wizard checks them before the first import.",
    checks: [
      'files.flight_plan',
      'files.boundaries',
      'files.geosoft',
      'files.geosoft_databases',
      'files.geosoft_zone',
      'files.geosoft_templates',
      'basemag.diurnal',
    ],
  },
];

/** One check of a step: a quick-start check, or the wizard's own (`client.org`). */
export interface SetupItem {
  readonly id: string;
  readonly title: string;
  readonly state: ChecklistState;
  readonly word: string;
  readonly problems: readonly string[];
  readonly found: string;
}

export interface SetupConfirmation {
  readonly step: string;
  readonly confirmed_by_name: string | null;
  readonly confirmed_at: string;
}

export interface ProjectSetupStep extends ProjectSetupStepDef {
  readonly items: readonly SetupItem[];
  /** The worst of its items (pass with none). */
  readonly state: ChecklistState;
  readonly confirmed: SetupConfirmation | null;
  /** Why it cannot be confirmed yet; null when it can. */
  readonly blocked: string | null;
}

export interface ProjectSetupInput {
  /** The checklist's input from the cloud's rows (`files`, `geosoftInstalled`, `diurnalRunning` null). */
  readonly checklist: ChecklistInput;
  /** The linked client organisation's name, null when none. */
  readonly clientOrgName: string | null;
  /** Its people with a sign-in (client admins and viewers); null when not read. */
  readonly clientPeople: number | null;
  /** The project's report emails (`project_report_email`), null: the default (both on). */
  readonly reportEmails: ReportEmailSetting | null;
  readonly confirmed: readonly SetupConfirmation[];
}

const worst = (states: readonly ChecklistState[]): ChecklistState =>
  states.includes('fail') ? 'fail' : states.includes('warn') ? 'warn' : 'pass';

const setupItem = (
  id: string,
  title: string,
  fails: readonly string[],
  warns: readonly string[],
  found: string,
): SetupItem => {
  const state: ChecklistState = fails.length ? 'fail' : warns.length ? 'warn' : 'pass';
  return { id, title, state, word: CHECKLIST_STATE_WORD[state], problems: [...fails, ...warns], found };
};

/** The client portal step's check: a client organisation linked, people who can sign in, the report emails. */
export function checkClientPortal(
  input: Pick<ProjectSetupInput, 'clientOrgName' | 'clientPeople' | 'reportEmails'>,
): SetupItem {
  if (!input.clientOrgName)
    return setupItem(
      'client.org',
      'Client organisation',
      [],
      [
        'No client organisation is linked: the client sees nothing of the project, published or shared, and nobody is emailed.',
      ],
      'No client organisation.',
    );
  const warns: string[] = [];
  if (input.clientPeople === 0)
    warns.push(
      `Nobody at ${input.clientOrgName} can sign in yet: invite them in Clients, or their client admin invites them.`,
    );
  const people =
    input.clientPeople === null
      ? ''
      : ` ${input.clientPeople} ${input.clientPeople === 1 ? 'person' : 'people'} can sign in.`;
  return setupItem(
    'client.org',
    'Client organisation',
    [],
    warns,
    `${input.clientOrgName} sees the published reports.${people} ${reportEmailSettingWords(input.reportEmails)}`,
  );
}

/** Every step of the wizard with its checks' states and whether it was confirmed. */
export function projectSetupWizard(input: ProjectSetupInput): ProjectSetupStep[] {
  const all = quickStartChecklist(input.checklist);
  return PROJECT_SETUP_STEPS.map((def) => {
    const items: SetupItem[] = def.checks
      .map((id) => all.find((i) => i.id === id))
      .filter((i): i is (typeof all)[number] => i !== undefined);
    if (def.id === 'client') items.push(checkClientPortal(input));
    const fails = items.filter((i) => i.state === 'fail').map((i) => i.title);
    return {
      ...def,
      items,
      state: worst(items.map((i) => i.state)),
      confirmed: input.confirmed.find((c) => c.step === def.id) ?? null,
      blocked:
        fails.length > 0
          ? `Fix the failed ${fails.length === 1 ? 'check' : 'checks'} first: ${fails.join(', ')}.`
          : null,
    };
  });
}

/** The counts of a step's checks, as `project_setup_step.checks` keeps them when it is confirmed. */
export function setupStepCounts(step: Pick<ProjectSetupStep, 'items'>): {
  readonly pass: number;
  readonly warn: number;
  readonly fail: number;
} {
  const n = (s: ChecklistState): number => step.items.filter((i) => i.state === s).length;
  return { pass: n('pass'), warn: n('warn'), fail: n('fail') };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayWords = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] ?? ''} ${m[1]}` : iso;
};

export interface SetupProgress {
  readonly confirmed: number;
  readonly total: number;
  readonly done: boolean;
  /** "5 of 8 set-up steps confirmed in Teslyn Cloud, last by …" and the like. */
  readonly text: string;
}

/**
 * The office's set-up in words, from the confirmations alone (Teslyn Field reads only `project_setup_step`): for the
 * wizard's header and Teslyn Field's Quick-start and training page.
 */
export function projectSetupProgress(confirmed: readonly SetupConfirmation[]): SetupProgress {
  const known = new Set<string>(PROJECT_SETUP_STEPS.map((s) => s.id));
  const mine = confirmed.filter((c) => known.has(c.step));
  const total = PROJECT_SETUP_STEPS.length;
  const last = [...mine].sort((a, b) => b.confirmed_at.localeCompare(a.confirmed_at))[0];
  const by = last
    ? `, last by ${last.confirmed_by_name ?? 'someone no longer in the organisation'} on ${dayWords(last.confirmed_at)}`
    : '';
  const done = mine.length === total;
  return {
    confirmed: mine.length,
    total,
    done,
    text:
      mine.length === 0
        ? 'Not set up in Teslyn Cloud yet: no step of the project set-up wizard is confirmed.'
        : done
          ? `Set up in Teslyn Cloud: every step confirmed${by}.`
          : `${mine.length} of ${total} set-up steps confirmed in Teslyn Cloud${by}.`,
  };
}
